import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { imagenNormalize } from '../../../Objetos/ImagenesModel';
import { normalizeValue } from '../../../../../utils/Texts';
import { getSessionUserId } from '../../../../../utils/SessionUser';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import Modal from './ImagenesManagerModal';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import AlertDialog from '../../../../Lms/Components/Shared/AlertDialog';
// Reutilizamos los mismos estilos premium
import '../../../Styles/ColoresManager.css';

const PAGE_SIZE = 12;
const endpoint = ENDPOINTS.imagenesSistema;
const endpointById = ENDPOINTS.imagenSistemaById;
const modelo = imagenNormalize;

export default function ImagenesManager() {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [query, setQuery] = useState('');
    const [page, setPage] = useState(1);

    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('new');
    const [selectedId, setSelectedId] = useState(null);
    const [editData, setEditData] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
    const [idEmpresa, setIdEmpresa] = useState(null);
    const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

    const triggerAlert = (message, variant = 'error', title = 'Error') => {
        setModalOpen(false);
        setDeleteOpen(false);
        setAlertConfig({ open: true, message, variant, title });
    };

    const loadData = async (cid = idEmpresa) => {
        setLoading(true);
        try {
            let url = endpoint;
            if (cid) url += `?idEmpresa=${cid}`;
            const res = await axios.get(url);
            const data = (res.data || []).map((x) => modelo(x));
            setRows(data);
        } catch (error) {
            console.error('Error al cargar imágenes:', error);
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const init = async () => {
            let foundId = null;
            const uid = getSessionUserId();
            if (uid) {
                try {
                    const { ENDPOINTS: GEN_ENDPOINTS } = await import('../../../../General/Utils/Endpoints');
                    const res = await axios.get(GEN_ENDPOINTS.empresasUsuario(uid));
                    if (res.data && res.data.length > 0) {
                        foundId = res.data[0].idEmpresa || res.data[0].IdEmpresa;
                        setIdEmpresa(foundId);
                    }
                } catch (e) { console.error('Error fetching company:', e); }
            }
            loadData(foundId);
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filtered = useMemo(() => {
        let data = rows;
        if (query.trim()) {
            const q = normalizeValue(query);
            data = data.filter((e) => {
                const nom = normalizeValue(e.nombre);
                const desc = normalizeValue(e.descripcion);
                const tipo = normalizeValue(e.nombreTipoImagen);
                return nom.includes(q) || desc.includes(q) || tipo.includes(q);
            });
        }
        return data;
    }, [rows, query]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const paginated = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return filtered.slice(start, start + PAGE_SIZE);
    }, [filtered, page]);

    const usedTypeIds = useMemo(() => {
        return rows.map(r => Number(r.idTipoImagen));
    }, [rows]);

    const handleNew = () => {
        setSelectedId(null);
        setEditData(null);
        setModalMode('new');
        setModalOpen(true);
    };

    const handleEdit = (item) => {
        setSelectedId(item.idImagen);
        setEditData(item);
        setModalMode('edit');
        setModalOpen(true);
    };

    const askDelete = (id, name) => {
        setDeleteTarget({ id, name });
        setDeleteOpen(true);
    };

    const doDelete = async () => {
        try {
            setLoading(true);
            await axios.delete(endpointById(deleteTarget.id));
            setDeleteOpen(false);
            await loadData();
        } catch (e) {
            console.error(e);
            triggerAlert('Error al eliminar');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (form, mode) => {
        try {
            setLoading(true);
            const payload = {
                IdImagen: mode === 'new' ? 0 : selectedId,
                Nombre: form.nombre,
                Descripcion: form.descripcion,
                IdTipoImagen: form.idTipoImagen,
                ImagenUrl: form.imagenUrl,
                Activo: form.activo ?? true,
                IdEmpresa: idEmpresa
            };

            if (mode === 'new') {
                await axios.post(endpoint, payload);
            } else {
                await axios.put(endpoint, payload);
            }
            setModalOpen(false);
            await loadData();
        } catch (e) {
            console.error(e);
            triggerAlert('Error al guardar');
        } finally {
            setLoading(false);
        }
    };

    const pageNumbers = (() => {
        const maxToShow = 5;
        let start = Math.max(1, page - 2);
        let end = Math.min(totalPages, start + maxToShow - 1);
        start = Math.max(1, end - maxToShow + 1);
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    })();

    return (
        <div className="colores-catalog-container">
            <header className="colores-catalog-header">
                <h2 className="colores-catalog-title">Imágenes del Sistema</h2>
                <div className="colores-catalog-search-wrap">
                    <span className="colores-catalog-search-icon">🔍</span>
                    <input
                        className="colores-catalog-search"
                        placeholder="Buscar por nombre o tipo..."
                        value={query}
                        onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                    />
                </div>
            </header>

            {loading ? (
                <div className="catalog-loading" style={{ textAlign: 'center', padding: '4rem', fontSize: '1.2rem', color: '#64748b' }}>
                    Cargando galería...
                </div>
            ) : (
                <div className="colores-grid">
                    {paginated.length === 0 ? (
                        <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '4rem', background: '#fff', borderRadius: '20px', border: '1px dashed #cbd5e1' }}>
                            <p style={{ color: '#94a3b8', fontSize: '1.1rem' }}>No se encontraron imágenes.</p>
                        </div>
                    ) : (
                        paginated.map((e) => (
                            <article
                                key={e.idImagen}
                                className="color-card"
                                style={{ height: 'auto', minHeight: '280px', cursor: 'pointer' }}
                                onClick={() => handleEdit(e)}
                            >
                                <div className="color-card-header" style={{ alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8fafc', borderRadius: '12px', padding: '1rem', height: '140px' }}>
                                    <img
                                        src={e.imagenUrl}
                                        alt={e.nombre}
                                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                    />
                                </div>

                                <div style={{ marginTop: '1rem' }}>
                                    <h3 className="color-card-title" style={{ marginBottom: '0.25rem' }}>{e.nombre}</h3>
                                    <span style={{ fontSize: '0.8rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>{e.nombreTipoImagen}</span>
                                </div>
                            </article>
                        ))
                    )}
                </div>
            )}

            {totalPages > 1 && (
                <div className="roles-pagination" style={{ marginTop: '2rem', justifyContent: 'center' }}>
                    <div className="roles-pagination-right">
                        <button
                            className={`roles-page-btn ${page === 1 ? 'is-disabled' : ''}`}
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                        >‹</button>
                        {pageNumbers.map(n => (
                            <button
                                key={n}
                                className={`roles-page-btn ${n === page ? 'is-active' : ''}`}
                                onClick={() => setPage(n)}
                            >{n}</button>
                        ))}
                        <button
                            className={`roles-page-btn ${page === totalPages ? 'is-disabled' : ''}`}
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                        >›</button>
                    </div>
                </div>
            )}

            {modalOpen && (
                <Modal
                    open={modalOpen}
                    mode={modalMode}
                    data={editData}
                    onClose={() => setModalOpen(false)}
                    onSubmit={handleSubmit}
                    usedTypes={usedTypeIds}
                />
            )}

            <ConfirmDeleteDialog
                open={deleteOpen}
                roleName={deleteTarget.name}
                onClose={() => setDeleteOpen(false)}
                onConfirm={doDelete}
            />

            <AlertDialog
                open={alertConfig.open}
                title={alertConfig.title}
                message={alertConfig.message}
                variant={alertConfig.variant}
                onClose={() => setAlertConfig({ ...alertConfig, open: false })}
            />
        </div>
    );
}
